/**
 * CiResults — search-result cards for the competitive-intel (CI) corpus.
 *
 * CI documents (company product pages + press releases) live entirely
 * outside Label Doctor's own viewers, so — unlike every other corpus this
 * app renders — a result row's primary action is an EXTERNAL link to the
 * competitor's own site, not an in-app navigation. ``safeCompetitiveIntelUrl``
 * is the single gate: only a URL that parses and is ``https:`` ever becomes
 * a real ``<a>``; anything else renders the row with no link at all.
 *
 * Visually mirrors the former SBA document results (card shell, score badge,
 * empty/loading/no-results states, Previous/Next pagination) so the two
 * "document search" surfaces read as one family.
 */
import { useState } from 'react';
import { Building2, FileText, Newspaper, Search } from 'lucide-react';
import type { CIDocumentResult } from '../types';
import { safeCompetitiveIntelUrl } from '../utils/competitiveIntelUrl';

interface CiResultsProps {
  documents: CIDocumentResult[];
  isLoading: boolean;
  isNoQuery: boolean;
  totalDocuments: number;
  currentPage?: number;
  pageSize?: number;
  onPageChange?: (page: number) => void;
}

const DOC_TYPE_LABEL: Record<CIDocumentResult['doc_type'], string> = {
  product_page: 'Product Page',
  press_release: 'Press Release',
};

function formatFetchedAt(value: string | null): string | null {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

export default function CiResults({
  documents,
  isLoading,
  isNoQuery,
  totalDocuments,
  currentPage = 1,
  pageSize = 20,
  onPageChange,
}: CiResultsProps) {
  // Per-card "Show more"/"Show less" for a long evidence snippet — uses a
  // 200-char clamp so a single verbose snippet
  // can't turn a result card into a wall of text.
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());
  const toggleExpanded = (key: string) => {
    setExpandedCards((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const totalPages = Math.max(1, Math.ceil(totalDocuments / pageSize));
  // CI scores are raw, un-normalized RRF (backend `ci_search_service.py`:
  // coalesce(v.s,0)+coalesce(f.s,0), each arm 1.0/(60+rank)) — NOT a 0..1
  // confidence the way SBA's server-side-normalized `final_score` is (SBA
  // divides by the page's max score server-side; CI ships the raw value).
  // A perfect double-match ceilings at ~2/61 ≈ 0.033, so rendering the raw
  // score as a percentage would show "3%" for the BEST possible result and
  // "0-1%" for everything else — misleadingly reads as "no good matches"
  // even when the ranking itself is fine. Normalize client-side, relative
  // to this page's top score, instead: the top result on any page always
  // reads 100%, lower results scale proportionally. (Deliberately NOT a
  // backend change — `_format_ci_result` is out of this frontend task's
  // scope, and a relative badge stays meaningful regardless of how the
  // backend's RRF constant/formula evolves.)
  const maxScore = documents.reduce((max, doc) => Math.max(max, doc.score), 0);
  const relevancePercent = (score: number): number =>
    maxScore > 0 ? Math.round((score / maxScore) * 100) : 0;

  // Empty state: no query entered
  if (isNoQuery) {
    return (
      <div className="flex flex-col items-center justify-center py-20 px-8 text-center">
        <div className="w-16 h-16 rounded-full bg-gradient-to-br from-indigo-50 to-purple-50 flex items-center justify-center mb-6">
          <Search className="w-8 h-8 text-indigo-400" />
        </div>
        <h3 className="text-lg font-semibold text-slate-700 mb-2">
          Search Competitive Intel
        </h3>
        <p className="text-slate-500 max-w-md">
          Enter a query to search across tracked competitor product pages
          and press releases.
        </p>
      </div>
    );
  }

  // Loading state
  if (isLoading && documents.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <div className="relative mb-4">
          <div className="w-10 h-10 border-3 border-indigo-200 rounded-full"></div>
          <div className="absolute top-0 left-0 w-10 h-10 border-3 border-indigo-500 rounded-full border-t-transparent animate-spin"></div>
        </div>
        <p className="text-sm text-slate-600 font-medium">Searching...</p>
      </div>
    );
  }

  // No results
  if (documents.length === 0 && !isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="w-14 h-14 rounded-full bg-slate-50 flex items-center justify-center mb-4">
          <FileText className="w-7 h-7 text-slate-300" />
        </div>
        <h3 className="text-base font-medium text-slate-600 mb-1">
          No competitive-intel documents found
        </h3>
        <p className="text-sm text-slate-400">Try adjusting your search terms.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Results header */}
      <div className="flex items-center justify-between px-1">
        <p className="text-sm text-slate-500">
          {totalDocuments} document{totalDocuments !== 1 ? 's' : ''} found
          {isLoading ? ' so far...' : ''}
        </p>
      </div>

      {/* Document cards */}
      {documents.map((doc) => {
        const safeUrl = safeCompetitiveIntelUrl(doc.page_url);
        const fetchedLabel = formatFetchedAt(doc.fetched_at);
        const title = doc.title || doc.company_name;
        const isExpanded = expandedCards.has(doc.result_key);
        const evidenceItems = doc.evidence.slice(0, 3);
        const hasLongSnippet = evidenceItems.some((ev) => ev.snippet.length > 200);
        return (
          <div
            key={doc.result_key}
            data-testid="ci-result-card"
            className="bg-white border border-slate-200 rounded-xl hover:border-indigo-200 hover:shadow-md transition-all px-5 py-4"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 text-xs font-medium mb-1">
                  <span
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full ${
                      doc.doc_type === 'press_release'
                        ? 'bg-purple-50 text-purple-600 ring-1 ring-purple-100'
                        : 'bg-emerald-50 text-emerald-600 ring-1 ring-emerald-100'
                    }`}
                  >
                    {doc.doc_type === 'press_release' ? (
                      <Newspaper className="w-3 h-3" />
                    ) : (
                      <Building2 className="w-3 h-3" />
                    )}
                    {DOC_TYPE_LABEL[doc.doc_type]}
                  </span>
                  {fetchedLabel && <span className="text-slate-400">{fetchedLabel}</span>}
                </div>
                {safeUrl ? (
                  <a
                    href={safeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-base font-semibold text-slate-800 hover:text-indigo-700 transition-colors"
                  >
                    {title}
                  </a>
                ) : (
                  <span className="text-base font-semibold text-slate-800">{title}</span>
                )}
                <div className="mt-0.5 text-sm text-slate-500">{doc.company_name}</div>
              </div>

              {/* Score */}
              <div className="flex-shrink-0 ml-4 text-right">
                <div className="text-sm font-semibold text-indigo-600">
                  {relevancePercent(doc.score)}%
                </div>
                <div className="text-xs text-slate-400 mt-0.5">relevance</div>
              </div>
            </div>

            {evidenceItems.length > 0 && (
              <div className="mt-3 pt-3 border-t border-slate-100 space-y-1.5">
                {evidenceItems.map((ev) => (
                  <p key={ev.chunk_id} className="text-sm text-slate-600 leading-relaxed">
                    {isExpanded || ev.snippet.length <= 200
                      ? ev.snippet
                      : `${ev.snippet.slice(0, 200)}...`}
                  </p>
                ))}
                {hasLongSnippet && (
                  <button
                    type="button"
                    className="text-xs text-indigo-500 hover:text-indigo-700 transition-colors"
                    onClick={() => toggleExpanded(doc.result_key)}
                  >
                    {isExpanded ? 'Show less' : 'Show more'}
                  </button>
                )}
              </div>
            )}
          </div>
        );
      })}

      {onPageChange && totalPages > 1 && (
        <div className="flex items-center justify-between px-1 pt-2">
          <button
            type="button"
            onClick={() => onPageChange(Math.max(1, currentPage - 1))}
            disabled={isLoading || currentPage <= 1}
            className="px-3 py-1.5 text-sm font-medium rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            Previous
          </button>
          <span className="text-sm text-slate-500">
            Page {currentPage} of {totalPages}
          </span>
          <button
            type="button"
            onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
            disabled={isLoading || currentPage >= totalPages}
            className="px-3 py-1.5 text-sm font-medium rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
